import { CloseIcon } from 'assets/icons/CloseIcon'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { ISelectValue } from 'constants/globalTypes'
import { createKitchenIngredient, editKitchenIngredient } from 'features/food/foodSlice'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { OtherText, TitleText } from 'styling/Typography'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import useModalClose from 'hooks/useModalClose'
import { IIngredientsModalState } from 'types/food.interface'
import { isRecipeUnitName } from './foodPlanningData'

const parseInventoryItems = (data: any) =>
  (data?.data || []).map((i: any) => ({ label: i.name, value: i.id }))

const parseUnits = (data: any) =>
  (Array.isArray(data) ? data : data?.data || [])
    .filter((i: any) => isRecipeUnitName(i?.name))
    .map((i: any) => ({ label: i.name, value: i.id }))

export const IngredientsModal = ({
  closeModal,
  value,
}: {
  closeModal: any
  value?: IIngredientsModalState
}) => {
  const [state, setState] = useState<IIngredientsModalState>(
    value || { name: '', sku: null, unit: null, stockToRecipeFactor: 1 },
  )
  const dispatch = useDispatch<AppDispatch>()
  useFocusFirstInput()
  useModalClose(closeModal)

  const handleSave = async () => {
    const body = {
      name: state.name,
      skuId: state.sku.value,
      unitId: state.unit.value,
      stockToRecipeFactor: state.stockToRecipeFactor,
    }
    const resp = value?.id
      ? await dispatch(editKitchenIngredient({ id: value.id, body }))
      : await dispatch(createKitchenIngredient(body))
    if (resp.meta.requestStatus === 'fulfilled') {
      closeModal()
    }
  }

  const invalid =
    !state.name || !state?.sku?.value || !state?.unit?.value || !(state.stockToRecipeFactor > 0)

  return (
    <Modal black>
      <Flex column between center>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold'>{value?.id ? 'Edit' : 'Add'} Ingredient</TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <Input
            labelText='Ingredient name'
            placeholder='Milk, sugar, salt...'
            value={state.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setState((prev) => ({ ...prev, name: e.target.value }))
            }
            nomarg
            invalid={!state.name}
          />
          <HonestSelectAsyncInput
            link='inventory/item'
            name='sku'
            labelText='Inventory item (SKU)'
            placeholder='Stock item this ingredient is stored as'
            defaultValue={state?.sku?.label}
            onChange={(sku: ISelectValue) => setState((prev) => ({ ...prev, sku }))}
            invalid={!state.sku}
            parserFunction={parseInventoryItems}
          />
          <HonestSelectAsyncInput
            link='units'
            name='unit'
            labelText='Recipe unit'
            placeholder='g, kg, ml...'
            defaultValue={state?.unit?.label}
            onChange={(unit: ISelectValue) => setState((prev) => ({ ...prev, unit }))}
            invalid={!state.unit}
            parserFunction={parseUnits}
          />
          <Input
            labelText='Stock to recipe factor'
            type='number'
            value={state.stockToRecipeFactor}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setState((prev) => ({ ...prev, stockToRecipeFactor: e.target.valueAsNumber }))
            }
            invalid={!(state.stockToRecipeFactor > 0)}
            nomarg
          />
          <OtherText grey>
            How many recipe units are in one stock unit (stock in kg, recipes in grams gives 1000).
          </OtherText>
        </Flex>
        <Button disabled={invalid} variant='primary' onClick={handleSave}>
          Save
        </Button>
      </Flex>
    </Modal>
  )
}
